import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import { useMemo, useState } from "react"
import { extraColors } from "../../../utils/theme"
import { formatBiothreatActiveDateRange } from "../biothreatActiveDateRange"
import { formatNwssSignalDescription } from "../nwssDisplayFormat"
import { ImpactScoreBadge } from "../PrecomputedSharedColumns"
import {
  DrawerSectionLabel,
  DrawerVariantProvider,
  precomputedDrawerPaperSx,
  summaryChipSx,
  type DrawerVariant,
} from "../precomputedDrawerStyles"
import type { PrecomputedTab } from "../types"
import {
  classifyImpact,
  getDiseaseLabel,
  getImpact,
  getNwssDetections,
  getNwssSites,
  getNwssStatus,
} from "./biothreatV2Signal"
import type { LocationGroup } from "./biothreatV2Groups"
import {
  V2_FONT_MONO,
  V2_NUMERIC,
  V2_SEVERITY_COLOR,
  V2_SEVERITY_LABEL,
  V2_SEVERITY_ORDER,
  V2_STATUS_COLOR,
  V2_STATUS_LABEL,
  V2_SURFACE,
  V2_TEXT,
  type V2NwssStatus,
  type V2Tier,
} from "./biothreatV2Tokens"

const GROUP_DRAWER_WIDTH = 460

/** Segment colors for the disease-composition bar (cycles for long mixes). */
const COMPOSITION_PALETTE = [
  "#37B2FF",
  "#2DE0A6",
  "#FFCE64",
  "#7EE6FF",
  "#9B8CFF",
  "#FF8FA3",
  "#5FD0C8",
  "#C0CBD8",
] as const

const median = (xs: number[]): number => {
  if (xs.length === 0) return 0
  const sorted = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1
    ? (sorted[mid] ?? 0)
    : Math.round(((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2)
}

const signalTitle = (signal: BiothreatSignal): string => {
  const raw =
    signal.source === "nwss"
      ? formatNwssSignalDescription(signal.title)
      : signal.title
  return raw.trim() || signal.description?.trim() || "Untitled signal"
}

interface BiothreatV2GroupDrawerProps {
  group: LocationGroup | null
  kind: PrecomputedTab
  /** The signal whose detail drawer is open (highlights its row), if any. */
  selectedSignalId?: string | null
  /** Drill into a single signal's detail drawer. */
  onSelectSignal: (signal: BiothreatSignal) => void
  onClose: () => void
  variant?: DrawerVariant
}

export function BiothreatV2GroupDrawer({
  group,
  kind,
  selectedSignalId,
  onSelectSignal,
  onClose,
  variant = "default",
}: BiothreatV2GroupDrawerProps) {
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={group !== null}
      onClose={onClose}
      slotProps={{
        paper: { sx: precomputedDrawerPaperSx(GROUP_DRAWER_WIDTH, variant) },
      }}
    >
      {group && (
        <DrawerVariantProvider value={variant}>
          {/* Keyed by location so the disease filter resets when a different
              cluster's drawer opens. */}
          <GroupDrawerContent
            key={group.key}
            group={group}
            kind={kind}
            selectedSignalId={selectedSignalId}
            onSelectSignal={onSelectSignal}
            onClose={onClose}
          />
        </DrawerVariantProvider>
      )}
    </Drawer>
  )
}

function GroupDrawerContent({
  group,
  kind,
  selectedSignalId,
  onSelectSignal,
  onClose,
}: {
  group: LocationGroup
  kind: PrecomputedTab
  selectedSignalId?: string | null
  onSelectSignal: (signal: BiothreatSignal) => void
  onClose: () => void
}) {
  // Null = show every disease; otherwise the signal list and profile narrow to
  // signals whose disease label matches (driven by the composition legend).
  const [selectedDisease, setSelectedDisease] = useState<string | null>(null)
  const filteredSignals = useMemo(
    () =>
      selectedDisease == null
        ? group.signals
        : group.signals.filter(s => getDiseaseLabel(s) === selectedDisease),
    [group.signals, selectedDisease]
  )

  return (
    <>
      <GroupHeader group={group} onClose={onClose} />
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Stack spacing={2.5}>
          <DiseaseComposition
            group={group}
            selectedDisease={selectedDisease}
            onToggleDisease={label =>
              setSelectedDisease(prev => (prev === label ? null : label))
            }
          />
          <GroupAggregates signals={filteredSignals} kind={kind} />
          <Divider sx={{ borderColor: extraColors.translucent5 }} />
          <GroupSignalList
            signals={filteredSignals}
            kind={kind}
            selectedDisease={selectedDisease}
            onClearDisease={() => setSelectedDisease(null)}
            selectedSignalId={selectedSignalId}
            onSelectSignal={onSelectSignal}
          />
        </Stack>
      </Box>
    </>
  )
}

function GroupHeader({
  group,
  onClose,
}: {
  group: LocationGroup
  onClose: () => void
}) {
  const topDisease = group.diseases[0]
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        mb: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h3Bold" sx={{ lineHeight: 1.3 }}>
          {group.locationLabel}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: extraColors.translucent50, display: "block", mt: 0.25 }}
        >
          {group.count} {group.count === 1 ? "signal" : "signals"}
          {topDisease && (
            <Box component="span" sx={{ textTransform: "capitalize" }}>
              {" · "}
              {group.diseases.length === 1
                ? topDisease.label
                : `${group.diseases.length} diseases`}
            </Box>
          )}
        </Typography>
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        aria-label="Close drawer"
        sx={{ color: extraColors.white, mt: "-4px", mr: "-4px" }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}

/**
 * Horizontal segmented bar of the disease mix + a labeled legend. The legend
 * doubles as a filter: clicking a disease narrows the profile and signal list
 * below to that disease (clicking the active one clears it). A single-disease
 * group has nothing to filter, so the legend stays inert there.
 */
function DiseaseComposition({
  group,
  selectedDisease,
  onToggleDisease,
}: {
  group: LocationGroup
  selectedDisease: string | null
  onToggleDisease: (label: string) => void
}) {
  if (group.diseases.length === 0) return null
  const total = group.diseases.reduce((sum, d) => sum + d.count, 0) || 1
  const filterable = group.diseases.length > 1
  return (
    <Box>
      <DrawerSectionLabel variant="caption" sx={{ mb: 1 }}>
        Disease composition
      </DrawerSectionLabel>
      <Box
        sx={{
          display: "flex",
          height: 12,
          borderRadius: "6px",
          overflow: "hidden",
          gap: "1px",
          bgcolor: V2_SURFACE.app,
        }}
      >
        {group.diseases.map((disease, i) => (
          <Box
            key={disease.label}
            title={`${disease.label}: ${disease.count}`}
            sx={{
              flexGrow: disease.count,
              flexBasis: 0,
              minWidth: 3,
              bgcolor: COMPOSITION_PALETTE[i % COMPOSITION_PALETTE.length],
              // Dim the segments not in focus when a disease is selected.
              opacity:
                selectedDisease == null || selectedDisease === disease.label
                  ? 1
                  : 0.3,
              transition: "opacity .12s",
            }}
          />
        ))}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", mt: 1 }}
      >
        {group.diseases.map((disease, i) => {
          const active = selectedDisease === disease.label
          const dimmed = selectedDisease != null && !active
          return (
            <Box
              key={disease.label}
              component={filterable ? "button" : "div"}
              type={filterable ? "button" : undefined}
              onClick={
                filterable ? () => onToggleDisease(disease.label) : undefined
              }
              aria-pressed={filterable ? active : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 0.75,
                py: 0.25,
                m: 0,
                borderRadius: "4px",
                border: `1px solid ${
                  active ? extraColors.translucentWhite : "transparent"
                }`,
                bgcolor: active ? "rgba(255,255,255,0.06)" : "transparent",
                cursor: filterable ? "pointer" : "default",
                opacity: dimmed ? 0.5 : 1,
                transition: "opacity .12s, background-color .12s",
                "&:hover": filterable
                  ? { bgcolor: "rgba(255,255,255,0.08)" }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "2px",
                  bgcolor: COMPOSITION_PALETTE[i % COMPOSITION_PALETTE.length],
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: V2_TEXT.body, textTransform: "capitalize" }}
              >
                {disease.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ...V2_NUMERIC, color: extraColors.translucent50 }}
              >
                {Math.round((disease.count / total) * 100)}%
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.25,
        py: 1,
        borderRadius: "6px",
        border: `1px solid ${V2_SURFACE.hairline}`,
        bgcolor: "rgba(255,255,255,0.03)",
      }}
    >
      <Typography
        sx={{
          ...V2_NUMERIC,
          fontSize: "1.15rem",
          color: extraColors.white,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: V2_FONT_MONO,
          fontSize: "0.6rem",
          letterSpacing: "0.1em",
          color: extraColors.translucent50,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  )
}

function GroupAggregates({
  signals,
  kind,
}: {
  signals: BiothreatSignal[]
  kind: PrecomputedTab
}) {
  if (kind === "nwss") return <NwssAggregates signals={signals} />
  return <GdeltAggregates signals={signals} />
}

function GdeltAggregates({ signals }: { signals: BiothreatSignal[] }) {
  const { tiles, tierChips } = useMemo(() => {
    const impacts = signals.map(getImpact)
    const sources = new Set(signals.map(s => s.source_name).filter(Boolean))
      .size
    const tierCounts: Record<V2Tier, number> = {
      severe: 0,
      high: 0,
      moderate: 0,
      low: 0,
    }
    for (const impact of impacts) tierCounts[classifyImpact(impact)] += 1
    return {
      tiles: {
        peak: Math.max(...impacts, 0),
        median: median(impacts),
        sources,
      },
      tierChips: V2_SEVERITY_ORDER.filter(tier => tierCounts[tier] > 0).map(
        tier => ({ tier, count: tierCounts[tier] })
      ),
    }
  }, [signals])

  return (
    <Box>
      <DrawerSectionLabel variant="caption" sx={{ mb: 1 }}>
        Impact profile
      </DrawerSectionLabel>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <StatTile label="Peak impact" value={tiles.peak} />
        <StatTile label="Median" value={tiles.median} />
        <StatTile label="Sources" value={tiles.sources} />
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {tierChips.map(({ tier, count }) => (
          <Chip
            key={tier}
            size="small"
            variant="outlined"
            label={`${V2_SEVERITY_LABEL[tier]} · ${count}`}
            sx={{
              ...summaryChipSx,
              borderColor: V2_SEVERITY_COLOR[tier],
              "& .MuiChip-label": {
                color: V2_SEVERITY_COLOR[tier],
                fontWeight: 700,
                fontFamily: V2_FONT_MONO,
                fontSize: "0.65rem",
                letterSpacing: "0.06em",
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  )
}

function NwssAggregates({ signals }: { signals: BiothreatSignal[] }) {
  const { detections, sites, statusChips, eventCount } = useMemo(() => {
    const statusCounts: Record<V2NwssStatus, number> = {
      start: 0,
      ongoing: 0,
      ended: 0,
    }
    let totalDetections = 0
    let totalSites = 0
    for (const signal of signals) {
      const status = getNwssStatus(signal)
      if (status) statusCounts[status] += 1
      totalDetections += getNwssDetections(signal)
      totalSites += getNwssSites(signal)
    }
    const order: V2NwssStatus[] = ["start", "ongoing", "ended"]
    return {
      detections: totalDetections,
      sites: totalSites,
      eventCount: signals.length,
      statusChips: order
        .filter(status => statusCounts[status] > 0)
        .map(status => ({ status, count: statusCounts[status] })),
    }
  }, [signals])

  return (
    <Box>
      <DrawerSectionLabel variant="caption" sx={{ mb: 1 }}>
        Detection profile
      </DrawerSectionLabel>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <StatTile label="Detections" value={detections} />
        <StatTile label="Sites" value={sites} />
        <StatTile label="Events" value={eventCount} />
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {statusChips.map(({ status, count }) => (
          <Chip
            key={status}
            size="small"
            variant="outlined"
            label={`${V2_STATUS_LABEL[status]} · ${count}`}
            sx={{
              ...summaryChipSx,
              borderColor: V2_STATUS_COLOR[status],
              "& .MuiChip-label": {
                color: V2_STATUS_COLOR[status],
                fontWeight: 700,
                fontFamily: V2_FONT_MONO,
                fontSize: "0.65rem",
                letterSpacing: "0.06em",
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  )
}

function GroupSignalList({
  signals,
  kind,
  selectedDisease,
  onClearDisease,
  selectedSignalId,
  onSelectSignal,
}: {
  signals: BiothreatSignal[]
  kind: PrecomputedTab
  selectedDisease: string | null
  onClearDisease: () => void
  selectedSignalId?: string | null
  onSelectSignal: (signal: BiothreatSignal) => void
}) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <DrawerSectionLabel variant="caption" sx={{ mb: 0 }}>
          Signals ({signals.length})
        </DrawerSectionLabel>
        {selectedDisease != null && (
          <Chip
            size="small"
            variant="outlined"
            label={selectedDisease}
            onDelete={onClearDisease}
            sx={{ ...summaryChipSx, textTransform: "capitalize" }}
          />
        )}
      </Box>
      <Stack spacing={1}>
        {signals.map(signal => (
          <GroupSignalRow
            key={signal.id}
            signal={signal}
            kind={kind}
            selected={signal.id === selectedSignalId}
            onSelect={onSelectSignal}
          />
        ))}
      </Stack>
    </Box>
  )
}

function GroupSignalRow({
  signal,
  kind,
  selected,
  onSelect,
}: {
  signal: BiothreatSignal
  kind: PrecomputedTab
  selected: boolean
  onSelect: (signal: BiothreatSignal) => void
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(signal)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        p: 1.25,
        borderRadius: "6px",
        border: `1px solid ${
          selected ? extraColors.status.navyLight : extraColors.translucent5
        }`,
        backgroundColor: selected
          ? extraColors.translucentActive
          : "rgba(255,255,255,0.03)",
        transition: "background-color .12s, border-color .12s",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.3,
            color: extraColors.white,
          }}
        >
          {signalTitle(signal)}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", mt: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: extraColors.status.navyLight,
              fontWeight: 700,
              textTransform: "capitalize",
              fontSize: "0.6rem",
            }}
          >
            {getDiseaseLabel(signal)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.45, fontSize: "0.6rem" }}
          >
            {formatBiothreatActiveDateRange(signal)}
          </Typography>
        </Stack>
      </Box>
      <GroupSignalBadge signal={signal} kind={kind} />
    </Box>
  )
}

function GroupSignalBadge({
  signal,
  kind,
}: {
  signal: BiothreatSignal
  kind: PrecomputedTab
}) {
  if (kind === "nwss") {
    return (
      <Box sx={{ textAlign: "center", flexShrink: 0 }}>
        <Typography sx={{ ...V2_NUMERIC, color: extraColors.white }}>
          {getNwssDetections(signal)}
        </Typography>
        <Typography
          sx={{
            fontFamily: V2_FONT_MONO,
            fontSize: "0.55rem",
            letterSpacing: "0.08em",
            color: extraColors.translucent50,
          }}
        >
          DETS
        </Typography>
      </Box>
    )
  }
  if (signal.impact_score == null) return null
  return (
    <Box sx={{ flexShrink: 0 }}>
      <ImpactScoreBadge score={signal.impact_score}>
        {signal.impact_score}
      </ImpactScoreBadge>
    </Box>
  )
}
